import { useEffect, useRef, useState } from 'react';
import { propertyApi } from '../services/api';

/**
 * 검색창 자동완성 — 백엔드 토큰 매칭 API 를 쓴다 (2026-09-24).
 *
 * 왜: 백엔드 `/api/properties/autocomplete/` 는 브랜드 토큰 분리·동의어·trigram 까지 하는데
 *     정작 지도·목록 검색창은 이걸 안 쓰고 단순 문자열 포함만 보고 있었다.
 *     그래서 '동대구역이편한세상' 같이 순서가 다르거나 이름이 다른 표기는 못 찾았다.
 *     (붙여 쓴 이름은 domain/property/searchMatch.ts 로컬 매칭으로도 잡히지만,
 *      순서 바뀜·동의어는 서버 쪽 점수 계산이 필요하다.)
 *
 * 동작
 *  - 200ms 디바운스. 2글자 미만이면 호출하지 않는다(API 규칙).
 *  - 응답이 늦게 와도 최신 검색어의 결과만 반영한다.
 *  - 실패하면 null 을 돌려준다 → 화면은 로컬 매칭 결과만 쓴다(검색이 멈추지 않는다).
 *  - 반환 순서는 서버 점수 순서 그대로 — 화면에서 이 순서를 살려 쓴다.
 */
const DEBOUNCE_MS = 200;
const MIN_LENGTH = 2;

type Answer = { q: string; ids: number[] | null };

export function useSearchSuggestions(query: string): number[] | null {
  const [answer, setAnswer] = useState<Answer>({ q: '', ids: null });
  const latest = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_LENGTH) {
      setAnswer({ q, ids: null });
      return;
    }
    const seq = ++latest.current;
    const timer = setTimeout(() => {
      propertyApi
        .autocomplete(q)
        .then((rows) => {
          if (seq !== latest.current) return; // 더 최신 검색어가 있다 → 버린다
          setAnswer({
            q,
            ids: Array.isArray(rows) ? rows.map((r) => Number(r.id)).filter((n) => !Number.isNaN(n)) : null,
          });
        })
        .catch(() => {
          if (seq !== latest.current) return;
          setAnswer({ q, ids: null }); // 네트워크 실패 → 로컬 매칭으로만 간다
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // ⛔ 검색어가 바뀐 뒤 아직 새 응답이 안 왔다면 **이전 검색어의 결과를 쓰지 않는다**.
  //    (axios 에 timeout 이 없어 응답이 영영 안 오면 옛 결과가 계속 섞여 보인다 —
  //     '푸르지오' 치다가 '자이' 로 바꿨는데 푸르지오가 남는 식)
  return answer.q === query.trim() ? answer.ids : null;
}

/**
 * 자동완성 결과 순서를 살려 정렬한다. 서버가 모르는 항목(로컬 매칭으로만 걸린 것)은 뒤로.
 */
export function orderBySuggestion<T extends { id: number | string }>(
  items: T[],
  suggestedIds: number[] | null,
): T[] {
  if (!suggestedIds || suggestedIds.length === 0) return items;
  const rank = new Map(suggestedIds.map((id, i) => [id, i]));
  return [...items].sort(
    (a, b) => (rank.get(Number(a.id)) ?? Number.MAX_SAFE_INTEGER) - (rank.get(Number(b.id)) ?? Number.MAX_SAFE_INTEGER),
  );
}
